import React from 'react';
import { Link } from 'react-router-dom';
import dashboardImage from './assets/linktime-dashboard.jpg';
import roomsImage from './assets/linktime-rooms.jpg';
import './LinkTimeLandingPage.css';

const comparisonRows = [
  {
    label: 'Connection',
    oldWay: 'Find a time on my calendar next week.',
    numFreeWay: 'Tap my link whenever you are ready to talk.',
  },
  {
    label: 'The interaction',
    oldWay: 'Wait for the clock to hit 2:00 PM.',
    numFreeWay: 'Knock when you are actually free.',
  },
  {
    label: 'The response',
    oldWay: 'Missed call or awkward reschedule.',
    numFreeWay: 'Be there in 5.',
  },
  {
    label: 'Privacy',
    oldWay: 'Your schedule becomes another data source.',
    numFreeWay: 'Only you and your guest know the visit happened.',
  },
];

const steps = [
  {
    title: 'Keep one room',
    body: 'Your LinkTime room stays ready. No new link for every chat, no back-and-forth over slots.',
  },
  {
    title: 'Let people knock',
    body: 'Guests arrive when they actually have the moment. You get a real-time nudge instead of another scheduling thread.',
  },
  {
    title: 'Answer on your terms',
    body: 'Jump in now or answer with a short buffer. The conversation stays warm without demanding instant availability.',
  },
];

const values = [
  {
    title: 'No calendar scraping',
    body: 'Your availability does not need to be exposed to Google, Microsoft, or anyone training models on your schedule.',
  },
  {
    title: 'Private interaction logs',
    body: 'The record of who visited and when belongs to you, not to an ad network or a workforce analytics dashboard.',
  },
  {
    title: 'Identity without Big Tech',
    body: 'Guests can be recognized without forcing every conversation through Sign in with Google.',
  },
];

function LinkTimeLandingPage() {
  return (
    <div className="linktime-landing">
      <section className="linktime-landing-hero">
        <div className="linktime-landing-hero-media" aria-hidden="true">
          <img src={dashboardImage} alt="" />
        </div>
        <div className="linktime-landing-hero-scrim" />

        <div className="linktime-landing-shell">
          <header className="linktime-landing-nav">
            <Link className="linktime-landing-brand" to="/">
              <span className="linktime-landing-brand-mark">#</span>
              <span>NumFree</span>
            </Link>

            <nav className="linktime-landing-nav-links" aria-label="LinkTime sections">
              <a href="#comparison">Why it feels better</a>
              <a href="#doorbell">How the knock works</a>
              <a href="#privacy">Privacy</a>
            </nav>

            <div className="linktime-landing-nav-actions">
              <Link className="linktime-landing-nav-link" to="/login">
                Sign in
              </Link>
              <Link className="linktime-landing-nav-cta" to="/signup">
                Claim your room
              </Link>
            </div>
          </header>

          <div className="linktime-landing-hero-copy">
            <p className="linktime-landing-eyebrow">Sovereign availability for real conversations</p>
            <h1>Tap my link when you&apos;re ready to talk.</h1>
            <p className="linktime-landing-subtitle">
              LinkTime brings back the spontaneity of a phone call without the spam of a phone number,
              the stiffness of scheduled slots, or the quiet surveillance of calendar scraping.
            </p>

            <div className="linktime-landing-hero-actions">
              <Link className="linktime-landing-primary" to="/signup">
                Start with LinkTime
              </Link>
              <a className="linktime-landing-secondary" href="#comparison">
                See the shift
              </a>
            </div>

            <div className="linktime-landing-hero-points">
              <span>Permanent room</span>
              <span>Real-time knock</span>
              <span>Private by design</span>
            </div>
          </div>
        </div>
      </section>

      <section className="linktime-landing-section linktime-landing-section-cream" id="comparison">
        <div className="linktime-landing-shell linktime-landing-stack">
          <div className="linktime-landing-section-intro">
            <p className="linktime-landing-kicker">The old way vs the NumFree way</p>
            <h2>Stop playing calendar Tetris.</h2>
            <p>
              Scheduled slots turned a simple reach-out into a bureaucratic transaction. LinkTime shifts the
              handshake from asynchronous planning to synchronous negotiation.
            </p>
          </div>

          <div className="linktime-landing-comparison" role="table" aria-label="Comparison between scheduled meetings and LinkTime">
            <div className="linktime-landing-comparison-head" role="rowgroup">
              <div role="columnheader">Moment</div>
              <div role="columnheader">Calendly or Zoom</div>
              <div role="columnheader">NumFree LinkTime</div>
            </div>
            {comparisonRows.map((row) => (
              <div className="linktime-landing-comparison-row" role="row" key={row.label}>
                <div className="linktime-landing-comparison-label" role="cell">{row.label}</div>
                <div className="linktime-landing-comparison-cell" role="cell">{row.oldWay}</div>
                <div className="linktime-landing-comparison-cell linktime-landing-comparison-cell-strong" role="cell">
                  {row.numFreeWay}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="linktime-landing-section" id="doorbell">
        <div className="linktime-landing-shell linktime-landing-grid">
          <div className="linktime-landing-section-intro">
            <p className="linktime-landing-kicker">The digital doorbell</p>
            <h2>Your room stays open. The answer stays yours.</h2>
            <p>
              The point is not instant interruption. The point is a live knock with enough buffer to stay human.
            </p>
            <div className="linktime-landing-steps">
              {steps.map((step) => (
                <article className="linktime-landing-step" key={step.title}>
                  <h3>{step.title}</h3>
                  <p>{step.body}</p>
                </article>
              ))}
            </div>
          </div>

          <figure className="linktime-landing-shot">
            <img src={roomsImage} alt="LinkTime rooms inside the NumFree dashboard with persistent links ready to share." />
            <figcaption>A persistent room beats a fresh booking link every time.</figcaption>
          </figure>
        </div>
      </section>

      <section className="linktime-landing-section linktime-landing-section-sage">
        <div className="linktime-landing-shell linktime-landing-stack">
          <div className="linktime-landing-section-intro">
            <p className="linktime-landing-kicker">Quick coffee, not boardroom theater</p>
            <h2>The guest gets a waiting room, not a black hole.</h2>
            <p>
              A knock should feel like standing at the door, not like sending another email into the void.
              LinkTime makes room for those five-minute buffers that real life needs.
            </p>
          </div>

          <div className="linktime-landing-signals">
            <div className="linktime-landing-signal">
              <strong>Distinct alert</strong>
              <span>A knock lands like a doorbell, not like another inbox chore.</span>
            </div>
            <div className="linktime-landing-signal">
              <strong>Visible status</strong>
              <span>Guests can tell whether you are stepping in now or arriving in a few minutes.</span>
            </div>
            <div className="linktime-landing-signal">
              <strong>Low-friction identity</strong>
              <span>The conversation starts without forcing everyone through a Big Tech sign-in funnel.</span>
            </div>
          </div>
        </div>
      </section>

      <section className="linktime-landing-section linktime-landing-section-clay" id="privacy">
        <div className="linktime-landing-shell linktime-landing-stack">
          <div className="linktime-landing-section-intro">
            <p className="linktime-landing-kicker">Data sovereignty</p>
            <h2>Your availability is not market research.</h2>
            <p>
              LinkTime keeps the benefit that disappears inside the big platforms: your timing, your guest list,
              and your interaction history stay in your hands.
            </p>
          </div>

          <div className="linktime-landing-values">
            {values.map((value) => (
              <article className="linktime-landing-value" key={value.title}>
                <h3>{value.title}</h3>
                <p>{value.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="linktime-landing-section linktime-landing-section-final">
        <div className="linktime-landing-shell linktime-landing-final">
          <div>
            <p className="linktime-landing-kicker">Ready when the moment is real</p>
            <h2>Give people one link and let the timing stay alive.</h2>
            <p>
              The speed of a call. The politeness of a meeting. None of the surveillance.
            </p>
          </div>
          <div className="linktime-landing-final-actions">
            <Link className="linktime-landing-primary" to="/signup">
              Get LinkTime
            </Link>
            <Link className="linktime-landing-secondary" to="/login">
              Sign in
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default LinkTimeLandingPage;
